import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

export default function Family() {
  const { t } = useLang();
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/family/members");
    setMembers(data.members);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    await api.delete(`/family/members/${id}`);
    load();
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-cap">— {t("app.sidebar2.family")}</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="family-title">
            {t("app.familyTitle")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-3xl">{t("app.familySub")}</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-outline" data-testid="family-new-btn">
          + {t("app.familyNew")}
        </button>
      </div>

      <div className="mt-10 space-y-px bg-[var(--border)]" data-testid="family-list">
        {members.length === 0 && (
          <p className="bg-[var(--bg)] py-10 text-center font-display-italic text-[var(--ink-soft)]" data-testid="family-empty">
            {t("app.familyEmpty")}
          </p>
        )}
        {members.map((m) => (
          <article key={m.id} className="bg-[var(--bg)] p-6 grid grid-cols-12 gap-4 items-baseline" data-testid={`family-member-${m.id}`}>
            <div className="col-span-12 md:col-span-3">
              <p className="label-cap text-[var(--oxblood)]">
                {t(`app.familyRoles.${m.role}`)}
              </p>
              {m.age != null && <p className="text-xs text-[var(--ink-faint)] mt-1">{m.age} anos</p>}
            </div>
            <div className="col-span-12 md:col-span-7">
              <p className="font-display text-2xl leading-snug">{m.name}</p>
              {m.notes && <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{m.notes}</p>}
              {m.whatsapp_phone && (
                <p className="text-xs text-[var(--ink-faint)] mt-2 tracking-monocle uppercase">
                  WhatsApp · {m.whatsapp_phone}
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-2 flex md:justify-end">
              <button onClick={() => remove(m.id)} className="btn-ghost" data-testid={`family-delete-${m.id}`}>×</button>
            </div>
          </article>
        ))}
      </div>

      {error && <p className="text-[var(--error)] text-sm mt-4">{error}</p>}

      {showNew && <NewMemberDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

const ROLES = ["spouse", "child", "parent", "in_law", "sibling", "other"];

function NewMemberDialog({ onClose, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    role: "child",
    age: "",
    notes: "",
    whatsapp_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        role: form.role,
        notes: form.notes || "",
        whatsapp_phone: form.whatsapp_phone || null,
        age: form.age ? Number(form.age) : null,
      };
      await api.post("/family/members", payload);
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[var(--ink)]/40 flex items-center justify-center px-4" onClick={onClose} data-testid="new-family-dialog">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[var(--surface)] border border-[var(--border)] p-10 w-full max-w-lg"
      >
        <p className="label-cap">— {t("app.familyNew")}</p>
        <div className="mt-6 space-y-5">
          <label className="block">
            <span className="label-cap">{t("app.familyName")}</span>
            <input className="line-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-family-name" />
          </label>
          <div>
            <p className="label-cap mb-2">{t("app.familyRole")}</p>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, role: r })}
                  data-testid={`new-family-role-${r}`}
                  className={`px-4 py-2 text-[11px] tracking-monocle uppercase border ${
                    form.role === r
                      ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                      : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                  }`}
                >
                  {t(`app.familyRoles.${r}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="label-cap">{t("app.familyAge")}</span>
              <input type="number" className="line-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="new-family-age" />
            </label>
            <label className="block">
              <span className="label-cap">{t("app.familyPhone")}</span>
              <input className="line-input" value={form.whatsapp_phone} onChange={(e) => setForm({ ...form, whatsapp_phone: e.target.value })} placeholder="+351..." data-testid="new-family-phone" />
            </label>
          </div>
          <label className="block">
            <span className="label-cap">{t("app.familyNotes")}</span>
            <textarea rows={2} className="line-input resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-family-notes" />
          </label>
        </div>
        {error && <p className="text-[var(--error)] text-sm mt-3">{error}</p>}
        <div className="flex justify-end gap-4 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost">{t("app.cancel")}</button>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="new-family-submit">
            {submitting ? "…" : t("app.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
